/**
 * Model Quality Validator for Schema GraphOS
 * Validates model quality and identifies potential issues
 */

/**
 * Calculate similarity between two strings (0-1, where 1 is identical)
 * Uses Levenshtein distance normalized
 */
const calculateSimilarity = (str1, str2) => {
  if (!str1 || !str2) return 0;
  
  const s1 = str1.toLowerCase().trim();
  const s2 = str2.toLowerCase().trim();
  
  if (s1 === s2) return 1;
  
  // Levenshtein distance
  const matrix = [];
  
  for (let i = 0; i <= s2.length; i++) {
    matrix[i] = [i];
  }
  
  for (let j = 0; j <= s1.length; j++) {
    matrix[0][j] = j;
  }
  
  for (let i = 1; i <= s2.length; i++) {
    for (let j = 1; j <= s1.length; j++) {
      if (s2.charAt(i - 1) === s1.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j] + 1
        );
      }
    }
  }
  
  const distance = matrix[s2.length][s1.length];
  const maxLength = Math.max(s1.length, s2.length);
  
  return 1 - (distance / maxLength);
};

/**
 * Check for duplicate entity names
 */
export const findDuplicateNames = (nodes) => {
  const issues = [];
  const nameMap = new Map();
  
  nodes.forEach(node => {
    const name = node.data?.name?.toLowerCase().trim();
    if (!name) return;
    
    if (nameMap.has(name)) {
      nameMap.get(name).push(node);
    } else {
      nameMap.set(name, [node]);
    }
  });
  
  nameMap.forEach((nodeList, name) => {
    if (nodeList.length > 1) {
      issues.push({
        type: 'duplicate_name',
        severity: 'critical',
        message: `Duplicate entity name: "${name}"`,
        entities: nodeList.map(n => ({ id: n.id, name: n.data?.name })),
        count: nodeList.length
      });
    }
  });
  
  return issues;
};

/**
 * Check for semantically similar names (potential typos or duplicates)
 */
export const findSimilarNames = (nodes, threshold = 0.85) => {
  const issues = [];
  const checked = new Set();
  
  for (let i = 0; i < nodes.length; i++) {
    for (let j = i + 1; j < nodes.length; j++) {
      const node1 = nodes[i];
      const node2 = nodes[j];
      
      const name1 = node1.data?.name;
      const name2 = node2.data?.name;
      
      if (!name1 || !name2) continue;
      
      const pairKey = `${Math.min(i, j)}-${Math.max(i, j)}`;
      if (checked.has(pairKey)) continue;
      checked.add(pairKey);
      
      const similarity = calculateSimilarity(name1, name2);
      
      if (similarity >= threshold && similarity < 1) {
        issues.push({
          type: 'similar_name',
          severity: 'warning',
          message: `Similar entity names detected`,
          entities: [
            { id: node1.id, name: name1 },
            { id: node2.id, name: name2 }
          ],
          similarity: Math.round(similarity * 100)
        });
      }
    }
  }
  
  return issues;
};

/**
 * Check for entities without primary keys
 */
export const findEntitiesWithoutPrimaryKey = (nodes) => {
  const issues = [];
  
  nodes.forEach(node => {
    // Skip text nodes
    if (node.type === 'text') return;
    
    const fields = node.data?.fields || [];
    const hasPK = fields.some(field => field.isPrimaryKey === true);
    
    if (!hasPK) {
      issues.push({
        type: 'no_primary_key',
        severity: 'critical',
        message: 'Entity without primary key',
        entities: [{ id: node.id, name: node.data?.name || 'Unnamed' }]
      });
    }
  });
  
  return issues;
};

/**
 * Check for entities without fields
 */
export const findEntitiesWithoutFields = (nodes) => {
  const issues = [];
  
  nodes.forEach(node => {
    // Skip text nodes
    if (node.type === 'text') return;
    
    const fields = node.data?.fields || [];
    
    if (fields.length === 0) {
      issues.push({
        type: 'no_fields',
        severity: 'critical',
        message: 'Entity without any fields',
        entities: [{ id: node.id, name: node.data?.name || 'Unnamed' }]
      });
    }
  });
  
  return issues;
};

/**
 * Check for entities without tags
 */
export const findEntitiesWithoutTags = (nodes) => {
  const issues = [];
  
  nodes.forEach(node => {
    // Skip text nodes
    if (node.type === 'text') return;
    
    const tags = node.data?.tags || [];
    
    if (tags.length === 0) {
      issues.push({
        type: 'no_tags',
        severity: 'info',
        message: 'Entity without tags',
        entities: [{ id: node.id, name: node.data?.name || 'Unnamed' }]
      });
    }
  });
  
  return issues;
};

/**
 * Check for entities without descriptions (table comments)
 */
export const findEntitiesWithoutDescriptions = (nodes) => {
  const issues = [];
  
  nodes.forEach(node => {
    // Skip text nodes
    if (node.type === 'text') return;
    
    const description = node.data?.description?.trim();
    
    if (!description) {
      issues.push({
        type: 'no_description',
        severity: 'warning',
        message: 'Entity without description',
        entities: [{ id: node.id, name: node.data?.name || 'Unnamed' }]
      });
    }
  });
  
  return issues;
};

/**
 * Check for fields without data types
 */
export const findFieldsWithoutTypes = (nodes) => {
  const issues = [];
  
  nodes.forEach(node => {
    // Skip text nodes
    if (node.type === 'text') return;
    
    const fields = node.data?.fields || [];
    const fieldsWithoutType = [];
    
    fields.forEach(field => {
      if (!field.type || field.type.trim() === '') {
        fieldsWithoutType.push(field.name || 'Unnamed field');
      }
    });
    
    if (fieldsWithoutType.length > 0) {
      issues.push({
        type: 'no_field_type',
        severity: 'critical',
        message: `Fields without data type in "${node.data?.name || 'Unnamed'}"`,
        entities: [{ id: node.id, name: node.data?.name || 'Unnamed' }],
        fields: fieldsWithoutType,
        count: fieldsWithoutType.length
      });
    }
  });
  
  return issues;
};

/**
 * Check for fields without descriptions
 */
export const findFieldsWithoutDescriptions = (nodes) => {
  const issues = [];
  
  nodes.forEach(node => {
    // Skip text nodes
    if (node.type === 'text') return;
    
    const fields = node.data?.fields || [];
    const fieldsWithoutDesc = [];
    
    fields.forEach(field => {
      if (!field.description || field.description.trim() === '') {
        fieldsWithoutDesc.push(field.name || 'Unnamed field');
      }
    });
    
    if (fieldsWithoutDesc.length > 0) {
      issues.push({
        type: 'no_field_description',
        severity: 'info',
        message: `Fields without description in "${node.data?.name || 'Unnamed'}"`,
        entities: [{ id: node.id, name: node.data?.name || 'Unnamed' }],
        fields: fieldsWithoutDesc,
        count: fieldsWithoutDesc.length
      });
    }
  });
  
  return issues;
};

/**
 * Check for relationships without cardinality
 */
export const findRelationshipsWithoutCardinality = (edges) => {
  const issues = [];
  
  edges.forEach(edge => {
    const cardinality = edge.data?.cardinality;
    
    if (!cardinality || cardinality.trim() === '') {
      issues.push({
        type: 'no_cardinality',
        severity: 'warning',
        message: 'Relationship without cardinality',
        relationship: {
          id: edge.id,
          source: edge.source,
          target: edge.target,
          label: edge.data?.label || 'Unnamed relationship'
        }
      });
    }
  });
  
  return issues;
};

/**
 * Check for unnamed entities
 */
export const findUnnamedEntities = (nodes) => {
  const issues = [];
  
  nodes.forEach(node => {
    // Skip text nodes
    if (node.type === 'text') return;
    
    const name = node.data?.name?.trim();
    
    if (!name || name === 'Unnamed' || name === '') {
      issues.push({
        type: 'unnamed_entity',
        severity: 'critical',
        message: 'Entity without a name',
        entities: [{ id: node.id, name: 'Unnamed' }]
      });
    }
  });
  
  return issues;
};

/**
 * Check for orphaned entities (no relationships)
 */
export const findOrphanedEntities = (nodes, edges) => {
  const issues = [];
  
  nodes.forEach(node => {
    // Skip text nodes
    if (node.type === 'text') return;
    
    const hasRelationship = edges.some(
      edge => edge.source === node.id || edge.target === node.id
    );
    
    if (!hasRelationship && nodes.length > 1) {
      issues.push({
        type: 'orphaned_entity',
        severity: 'warning',
        message: 'Entity with no relationships',
        entities: [{ id: node.id, name: node.data?.name || 'Unnamed' }]
      });
    }
  });
  
  return issues;
};

/**
 * Check for duplicate indexes (with the same fields)
 */
export const findDuplicateIndexes = (nodes) => {
  const issues = [];
  
  nodes.forEach(node => {
    // Skip text nodes
    if (node.type === 'text') return;
    
    const indexes = node.data?.indexes || [];
    if (indexes.length === 0) return;
    
    // Track which index pairs have already been reported
    const reported = new Set();
    
    // Compare each index with all others
    for (let i = 0; i < indexes.length; i++) {
      for (let j = i + 1; j < indexes.length; j++) {
        const index1 = indexes[i];
        const index2 = indexes[j];
        
        // Skip if indexes don't have columns array
        if (!index1 || !index1.columns || !Array.isArray(index1.columns) || index1.columns.length === 0) continue;
        if (!index2 || !index2.columns || !Array.isArray(index2.columns) || index2.columns.length === 0) continue;
        
        // Validate column structure - only require fieldName
        const hasValidColumns1 = index1.columns.every(c => c && c.fieldName);
        const hasValidColumns2 = index2.columns.every(c => c && c.fieldName);
        if (!hasValidColumns1 || !hasValidColumns2) continue;
        
        // Check if columns are identical (same fields and order)
        const cols1 = index1.columns.map(c => `${c.fieldName}_${c.order || 'ASC'}`).join('|');
        const cols2 = index2.columns.map(c => `${c.fieldName}_${c.order || 'ASC'}`).join('|');
        
        const pairKey = `${i}-${j}`;
        if (cols1 === cols2 && !reported.has(pairKey)) {
          reported.add(pairKey);
          issues.push({
            type: 'duplicate_index',
            severity: 'critical',
            message: `Duplicate indexes with same fields in "${node.data?.name || 'Unnamed'}"`,
            entities: [{ id: node.id, name: node.data?.name || 'Unnamed' }],
            fields: [`${index1.name || 'Unnamed'} (${index1.type || 'Unknown'})`, `${index2.name || 'Unnamed'} (${index2.type || 'Unknown'})`],
            count: 2
          });
        }
      }
    }
  });
  
  return issues;
};

/**
 * Check for indexes containing only the primary key column
 */
export const findIndexesOnlyWithPrimaryKey = (nodes) => {
  const issues = [];
  
  nodes.forEach(node => {
    // Skip text nodes
    if (node.type === 'text') return;
    
    const fields = node.data?.fields || [];
    const indexes = node.data?.indexes || [];
    
    if (indexes.length === 0) return;
    
    // Find primary key field(s)
    const pkFields = fields.filter(f => f && f.isPrimaryKey === true).map(f => f.name);
    
    if (pkFields.length === 0) return;
    
    // Check each index
    indexes.forEach(index => {
      // Skip if index doesn't have name (incomplete index)
      if (!index || !index.name) return;
      
      // Skip if index doesn't have columns array
      if (!index.columns || !Array.isArray(index.columns) || index.columns.length === 0) return;
      
      // Validate column structure
      const hasValidColumns = index.columns.every(c => c && c.fieldName);
      if (!hasValidColumns) return;
      
      // Index has only 1 column
      if (index.columns.length === 1) {
        const indexFieldName = index.columns[0].fieldName;
        
        // Check if it's a primary key
        if (pkFields.includes(indexFieldName)) {
          issues.push({
            type: 'index_only_pk',
            severity: 'critical',
            message: `Index contains only primary key in "${node.data?.name || 'Unnamed'}"`,
            entities: [{ id: node.id, name: node.data?.name || 'Unnamed' }],
            fields: [`${index.name || 'Unnamed'} (${index.type || 'Unknown'}) - Field: ${indexFieldName}`]
          });
        }
      }
    });
  });
  
  return issues;
};

/**
 * Check for indexes with all table fields
 */
export const findIndexesWithAllFields = (nodes) => {
  const issues = [];
  
  nodes.forEach(node => {
    // Skip text nodes
    if (node.type === 'text') return;
    
    const fields = node.data?.fields || [];
    const indexes = node.data?.indexes || [];
    
    if (indexes.length === 0 || fields.length === 0) return;
    
    const totalFields = fields.length;
    
    // Check each index
    indexes.forEach(index => {
      // Skip if index doesn't have name or columns array
      if (!index || !index.name) return;
      if (!index.columns || !Array.isArray(index.columns) || index.columns.length === 0) return;
      
      // Validate column structure
      const hasValidColumns = index.columns.every(c => c && c.fieldName);
      if (!hasValidColumns) return;
      
      // Index has same number of columns as table has fields
      if (index.columns.length === totalFields) {
        const indexFieldNames = index.columns.map(c => c.fieldName).sort();
        const tableFieldNames = fields.map(f => f.name).sort();
        
        // Check if all fields are included
        const allFieldsIncluded = indexFieldNames.every((name, idx) => name === tableFieldNames[idx]);
        
        if (allFieldsIncluded) {
          issues.push({
            type: 'index_all_fields',
            severity: 'info',
            message: `Index contains all table fields in "${node.data?.name || 'Unnamed'}"`,
            entities: [{ id: node.id, name: node.data?.name || 'Unnamed' }],
            fields: [`${index.name || 'Unnamed'} (${index.type || 'Unknown'}) - ${totalFields} fields`]
          });
        }
      }
    });
  });
  
  return issues;
};

/**
 * Main validation function - runs all checks
 */
export const validateModel = (nodes, edges) => {
  // Validate input parameters
  if (!nodes || !Array.isArray(nodes)) {
    throw new Error('Invalid nodes parameter');
  }
  
  if (!edges || !Array.isArray(edges)) {
    throw new Error('Invalid edges parameter');
  }
  
  const allIssues = [];
  
  // Run each validation with error handling
  const runValidation = (validationFn, name, ...args) => {
    try {
      const result = validationFn(...args);
      if (Array.isArray(result)) {
        allIssues.push(...result);
      }
    } catch (error) {
      throw new Error(`${name}: ${error.message}`);
    }
  };
  
  // Critical issues
  runValidation(findDuplicateNames, 'findDuplicateNames', nodes);
  runValidation(findUnnamedEntities, 'findUnnamedEntities', nodes);
  runValidation(findEntitiesWithoutPrimaryKey, 'findEntitiesWithoutPrimaryKey', nodes);
  runValidation(findEntitiesWithoutFields, 'findEntitiesWithoutFields', nodes);
  runValidation(findFieldsWithoutTypes, 'findFieldsWithoutTypes', nodes);
  runValidation(findDuplicateIndexes, 'findDuplicateIndexes', nodes);
  runValidation(findIndexesOnlyWithPrimaryKey, 'findIndexesOnlyWithPrimaryKey', nodes);
  
  // Warning issues
  runValidation(findSimilarNames, 'findSimilarNames', nodes);
  runValidation(findEntitiesWithoutDescriptions, 'findEntitiesWithoutDescriptions', nodes);
  runValidation(findRelationshipsWithoutCardinality, 'findRelationshipsWithoutCardinality', edges);
  runValidation(findOrphanedEntities, 'findOrphanedEntities', nodes, edges);
  
  // Info issues
  runValidation(findEntitiesWithoutTags, 'findEntitiesWithoutTags', nodes);
  runValidation(findFieldsWithoutDescriptions, 'findFieldsWithoutDescriptions', nodes);
  runValidation(findIndexesWithAllFields, 'findIndexesWithAllFields', nodes);
  
  // Count by severity
  const summary = {
    total: allIssues.length,
    critical: allIssues.filter(i => i.severity === 'critical').length,
    warning: allIssues.filter(i => i.severity === 'warning').length,
    info: allIssues.filter(i => i.severity === 'info').length
  };
  
  return {
    issues: allIssues,
    summary,
    passed: allIssues.filter(i => i.severity === 'critical').length === 0
  };
};

/**
 * Get severity color for UI
 */
export const getSeverityColor = (severity) => {
  const colors = {
    critical: {
      bg: 'bg-red-50 dark:bg-red-900/20',
      border: 'border-red-200 dark:border-red-800',
      text: 'text-red-700 dark:text-red-400',
      icon: 'text-red-600 dark:text-red-500'
    },
    warning: {
      bg: 'bg-yellow-50 dark:bg-yellow-900/20',
      border: 'border-yellow-200 dark:border-yellow-800',
      text: 'text-yellow-700 dark:text-yellow-400',
      icon: 'text-yellow-600 dark:text-yellow-500'
    },
    info: {
      bg: 'bg-blue-50 dark:bg-blue-900/20',
      border: 'border-blue-200 dark:border-blue-800',
      text: 'text-blue-700 dark:text-blue-400',
      icon: 'text-blue-600 dark:text-blue-500'
    }
  };
  
  return colors[severity] || colors.info;
};

/**
 * Get issue type label
 */
export const getIssueTypeLabel = (type) => {
  const labels = {
    duplicate_name: 'Duplicate Name',
    similar_name: 'Similar Names',
    no_primary_key: 'No Primary Key',
    no_fields: 'No Fields',
    no_tags: 'No Tags',
    no_description: 'No Description',
    no_field_type: 'Missing Field Type',
    no_field_description: 'Missing Field Description',
    no_cardinality: 'No Cardinality',
    unnamed_entity: 'Unnamed Entity',
    orphaned_entity: 'Orphaned Entity',
    duplicate_index: 'Duplicate Index',
    index_only_pk: 'Index Only on Primary Key',
    index_all_fields: 'Index with All Fields'
  };
  
  return labels[type] || type;
};
